import { EditorView, keymap } from "@codemirror/view";
import type {
    IntelligenceCodeAction,
    IntelligenceDocumentSymbol,
    SourceRange,
} from "@magic-md/engine-client/protocol";
import { t as translate } from "@magic-md/i18n";
import {
    extractDefinitionIdAtCursor,
    flattenDocumentSymbols,
    getAnchorCoords,
} from "./helpers";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";
import { useEditorStore } from "../../store/editor-store";
import {
    findMagicNavigationTargetLine,
    getMagicImportSourceFromClick,
    getMagicNavigationTargetFromClick,
    isMagicNavigationClick,
    resolveLocalImportSourcePath,
} from "../codemirror-magic-syntax/render/event-handlers";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

interface ReferenceMenuItem {
    label: string;
    line: number;
    column: number;
    endLine: number;
    endColumn: number;
}

interface SymbolMenuItem {
    label: string;
    detail?: string;
    line: number;
    column: number;
}

type ContextMenuIcon = "edit" | "search" | "wand";

export interface IntelligenceContextMenuItem {
    key: string;
    label: string;
    shortcut?: string;
    disabled?: boolean;
    separator?: boolean;
    icon: ContextMenuIcon;
    runAfterClose?: boolean;
    onSelect: () => void;
}

export interface IntelligenceRenameRequest {
    x: number;
    y: number;
    definitionId: string;
    initialValue: string;
    onSubmit: (nextName: string) => void;
}

declare global {
    interface WindowEventMap {
        "magic-md:intelligence-open-rename": CustomEvent<IntelligenceRenameRequest>;
        "magic-md:intelligence-show-context-menu": CustomEvent<{
            x: number;
            y: number;
            items: IntelligenceContextMenuItem[];
        }>;
        "magic-md:intelligence-show-references": CustomEvent<{
            x: number;
            y: number;
            items: ReferenceMenuItem[];
        }>;
        "magic-md:intelligence-show-symbols": CustomEvent<{
            x: number;
            y: number;
            items: SymbolMenuItem[];
        }>;
        "magic-md:intelligence-show-code-actions": CustomEvent<{
            x: number;
            y: number;
            actions: IntelligenceCodeAction[];
        }>;
    }
}

function showReferencesMenu(
    view: EditorView,
    references: SourceRange[],
    pos: number,
) {
    const items = references.map((range) => {
        const lineText = view.state.doc.line(range.startLine).text.trim();
        return {
            label: `L${range.startLine}: ${lineText || "(empty)"}`,
            line: range.startLine,
            column: range.startColumn,
            endLine: range.endLine,
            endColumn: range.endColumn,
        };
    });
    const anchor = getAnchorCoords(view, pos);
    window.dispatchEvent(
        new CustomEvent("magic-md:intelligence-show-references", {
            detail: { ...anchor, items },
        }),
    );
}

function showSymbolsMenu(
    view: EditorView,
    symbols: IntelligenceDocumentSymbol[],
    pos: number,
) {
    const items = flattenDocumentSymbols(symbols).map((symbol) => ({
        label: `${symbol.depth > 0 ? `${"· ".repeat(symbol.depth)}` : ""}${symbol.name}`,
        detail: symbol.detail,
        line: symbol.line,
        column: symbol.column,
    }));
    const anchor = getAnchorCoords(view, pos);
    window.dispatchEvent(
        new CustomEvent("magic-md:intelligence-show-symbols", {
            detail: { ...anchor, items },
        }),
    );
}

function showCodeActionsMenu(
    view: EditorView,
    actions: IntelligenceCodeAction[],
    pos: number,
) {
    const anchor = getAnchorCoords(view, pos);
    window.dispatchEvent(
        new CustomEvent("magic-md:intelligence-show-code-actions", {
            detail: { ...anchor, actions },
        }),
    );
}

function showContextMenu(
    x: number,
    y: number,
    items: IntelligenceContextMenuItem[],
    selection?: { text: string; range: { from: number; to: number } },
) {
    window.dispatchEvent(
        new CustomEvent("magic-md:intelligence-show-context-menu", {
            detail: { x, y, items, selection },
        }),
    );
}

function getSelectionContext(view: EditorView):
    | { text: string; range: { from: number; to: number } }
    | undefined {
    const range = view.state.selection.main;
    if (range.empty) return undefined;
    const from = Math.min(range.from, range.to);
    const to = Math.max(range.from, range.to);
    const text = view.state.sliceDoc(from, to);
    return text ? { text, range: { from, to } } : undefined;
}

export function intelligenceActions(config: IntelligenceConfig) {
    const resolveSession = createIntelligenceSessionResolver(config);

    const applyRenameAt = async (
        view: EditorView,
        pos: number,
        nextName: string,
    ): Promise<boolean> => {
        const engine = config.getEngine();
        if (!engine?.intelligenceRename) return false;

        const line = view.state.doc.lineAt(pos);
        const definitionId = extractDefinitionIdAtCursor(
            line.text,
            pos - line.from,
        );
        if (!definitionId) return false;

        const normalizedName = nextName.trim();
        if (!normalizedName || normalizedName === definitionId) return false;

        const session = await resolveSession();
        if (!session?.engine.intelligenceRename) return false;
        const result = await session.engine.intelligenceRename({
            sessionId: session.sessionId,
            definitionId,
            newName: normalizedName,
        });
        if (!result.changes) return false;

        const oldSelection = view.state.selection;
        const anchor = Math.min(oldSelection.main.anchor, result.newContent.length);
        const head = Math.min(oldSelection.main.head, result.newContent.length);
        view.dispatch({
            changes: {
                from: 0,
                to: view.state.doc.length,
                insert: result.newContent,
            },
            selection: { anchor, head },
        });
        return true;
    };

    const openRenameAt = (view: EditorView, pos: number): boolean => {
        const line = view.state.doc.lineAt(pos);
        const definitionId = extractDefinitionIdAtCursor(
            line.text,
            pos - line.from,
        );
        if (!definitionId) return false;

        const anchor = getAnchorCoords(view, pos);
        window.dispatchEvent(
            new CustomEvent("magic-md:intelligence-open-rename", {
                detail: {
                    x: anchor.x,
                    y: anchor.y,
                    definitionId,
                    initialValue: definitionId,
                    onSubmit: (nextName: string) => {
                        void applyRenameAt(view, pos, nextName);
                    },
                },
            }),
        );
        return true;
    };

    const gotoDefinitionAt = async (
        view: EditorView,
        pos: number,
    ): Promise<boolean> => {
        const engine = config.getEngine();
        if (!engine?.intelligenceDefinition) return false;

        const line = view.state.doc.lineAt(pos);
        const column = pos - line.from;
        const lineText = line.text;
        if (!extractDefinitionIdAtCursor(lineText, column)) return false;

        const session = await resolveSession();
        if (!session?.engine.intelligenceDefinition) return false;
        const result = await session.engine.intelligenceDefinition({
            sessionId: session.sessionId,
            line: line.number,
            column,
        });
        if (result?.filePath) {
            await useEditorStore.getState().openFileFromPath(result.filePath);
            return true;
        }
        if (!result?.range) return false;

        window.dispatchEvent(
            new CustomEvent("magic-md:goto-line", {
                detail: {
                    line: result.range.startLine,
                    flash: true,
                },
            }),
        );
        return true;
    };

    const gotoLocalMagicTarget = (
        view: EditorView,
        targetId: string | null,
    ): boolean => {
        if (!targetId) return false;
        const targetLine = findMagicNavigationTargetLine(view.state.doc, targetId);
        if (targetLine == null) return false;
        window.dispatchEvent(
            new CustomEvent("magic-md:goto-line", {
                detail: { line: targetLine, flash: true },
            }),
        );
        return true;
    };

    const openLocalImportSource = async (sourcePath: string | null): Promise<boolean> => {
        const filePath = resolveLocalImportSourcePath(
            config.getCurrentFilePath?.() ?? null,
            sourcePath,
        );
        if (!filePath) return false;
        await useEditorStore.getState().openFileFromPath(filePath);
        return true;
    };

    const showReferencesAt = async (
        view: EditorView,
        pos: number,
    ): Promise<boolean> => {
        const engine = config.getEngine();
        if (!engine?.intelligenceReferences) return false;

        const line = view.state.doc.lineAt(pos);
        const definitionId = extractDefinitionIdAtCursor(
            line.text,
            pos - line.from,
        );
        if (!definitionId) return false;

        const session = await resolveSession();
        if (!session?.engine.intelligenceReferences) return false;
        const result = await session.engine.intelligenceReferences({
            sessionId: session.sessionId,
            definitionId,
        });
        if (!result.references.length) return false;
        showReferencesMenu(view, result.references, pos);
        return true;
    };

    const showSymbols = async (view: EditorView): Promise<boolean> => {
        const engine = config.getEngine();
        if (!engine?.intelligenceSymbols) return false;

        const session = await resolveSession();
        if (!session?.engine.intelligenceSymbols) return false;
        const result = await session.engine.intelligenceSymbols({
            sessionId: session.sessionId,
            locale: config.getLocale?.(),
        });
        if (!result.symbols.length) return false;
        showSymbolsMenu(view, result.symbols, view.state.selection.main.head);
        return true;
    };

    const showCodeActionsAt = async (
        view: EditorView,
        pos: number,
    ): Promise<boolean> => {
        const engine = config.getEngine();
        if (!engine?.intelligenceCodeAction) return false;

        const line = view.state.doc.lineAt(pos);
        const session = await resolveSession();
        if (!session?.engine.intelligenceCodeAction) return false;
        const result = await session.engine.intelligenceCodeAction({
            sessionId: session.sessionId,
            line: line.number,
            column: pos - line.from,
            locale: config.getLocale?.(),
        });
        if (!result.actions.length) return false;
        showCodeActionsMenu(view, result.actions, pos);
        return true;
    };

    const showContextMenuAt = async (
        view: EditorView,
        event: MouseEvent,
    ): Promise<boolean> => {
        const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
        if (pos == null) return false;

        const engine = config.getEngine();
        const line = view.state.doc.lineAt(pos);
        const column = pos - line.from;
        const definitionId = extractDefinitionIdAtCursor(line.text, column);
        const onDiagnosticWidget =
            event.target instanceof HTMLElement &&
            event.target.closest(".cm-diag-inline, .cm-diag-inline-container, .cm-magic-error-msg") !== null;
        const selection = getSelectionContext(view);

        if (!definitionId && !onDiagnosticWidget && !selection) {
            return false;
        }

        const session = await resolveSession();
        if (!session) return false;

        const items: IntelligenceContextMenuItem[] = [];

        if (definitionId && engine?.intelligenceRename && session.engine.intelligenceRename) {
            items.push({
                key: `rename-${definitionId}`,
                label: tr("intelligence.rename", { id: definitionId }),
                shortcut: "F2",
                icon: "edit",
                onSelect: () => {
                    openRenameAt(view, pos);
                },
            });
        }

        if (definitionId && engine?.intelligenceReferences && session.engine.intelligenceReferences) {
            const referencesResult = await session.engine.intelligenceReferences({
                sessionId: session.sessionId,
                definitionId,
            });
            if (referencesResult.references.length > 0) {
                items.push({
                    key: `references-${definitionId}`,
                    label: tr("intelligence.findReferences", { count: referencesResult.references.length }),
                    shortcut: "Shift+F12",
                    icon: "search",
                    runAfterClose: true,
                    onSelect: () => {
                        showReferencesMenu(view, referencesResult.references, pos);
                    },
                });
            }
        }

        if (engine?.intelligenceCodeAction && session.engine.intelligenceCodeAction) {
            const result = await session.engine.intelligenceCodeAction({
                sessionId: session.sessionId,
                line: line.number,
                column,
                locale: config.getLocale?.(),
            });
            result.actions.forEach((action, index) => {
                items.push({
                    key: `code-action-${index}`,
                    label: action.title,
                    shortcut: action.isPreferred ? tr("intelligence.preferred") : undefined,
                    disabled: !action.edit,
                    separator: definitionId ? index === 0 : false,
                    icon: "wand",
                    onSelect: () => {
                        if (!action.edit) return;
                        const lineStart = view.state.doc.line(action.edit.range.startLine);
                        const lineEnd = view.state.doc.line(action.edit.range.endLine);
                        const from = Math.min(
                            lineStart.from + action.edit.range.startColumn,
                            lineStart.to,
                        );
                        const to = Math.min(
                            lineEnd.from + action.edit.range.endColumn,
                            lineEnd.to,
                        );
                        view.dispatch({
                            changes: {
                                from,
                                to,
                                insert: action.edit.newText,
                            },
                        });
                    },
                });
            });
        }

        if (items.length === 0 && !selection) {
            return false;
        }

        showContextMenu(event.clientX, event.clientY, items, selection);
        return true;
    };

    return [
        keymap.of([
            {
                key: "F12",
                run: (view) => {
                    void gotoDefinitionAt(view, view.state.selection.main.head);
                    return true;
                },
            },
            {
                key: "Shift-F12",
                run: (view) => {
                    void showReferencesAt(view, view.state.selection.main.head);
                    return true;
                },
            },
            {
                key: "F2",
                run: (view) => {
                    return openRenameAt(view, view.state.selection.main.head);
                },
            },
            {
                key: "Mod-Shift-o",
                run: (view) => {
                    void showSymbols(view);
                    return true;
                },
            },
            {
                key: "Mod-.",
                run: (view) => {
                    void showCodeActionsAt(view, view.state.selection.main.head);
                    return true;
                },
            },
        ]),
        EditorView.domEventHandlers({
            click(event, view) {
                if (!isMagicNavigationClick(event)) return false;
                const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
                const importSource = getMagicImportSourceFromClick(event, view);
                const localTargetId = getMagicNavigationTargetFromClick(event, view);
                if (pos == null && !localTargetId && !importSource) return false;

                void (async () => {
                    const handledByEngine = pos == null
                        ? false
                        : await gotoDefinitionAt(view, pos);
                    if (!handledByEngine && importSource) {
                        const opened = await openLocalImportSource(importSource);
                        if (opened) return;
                    }
                    if (!handledByEngine) {
                        gotoLocalMagicTarget(view, localTargetId);
                    }
                })();
                event.preventDefault();
                event.stopPropagation();
                return true;
            },
            contextmenu(event, view) {
                const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
                if (pos == null) return false;
                const line = view.state.doc.lineAt(pos);
                const column = pos - line.from;
                const definitionId = extractDefinitionIdAtCursor(line.text, column);
                const onDiagnosticWidget =
                    event.target instanceof HTMLElement &&
                    event.target.closest(".cm-diag-inline, .cm-diag-inline-container, .cm-magic-error-msg") !== null;
                const selection = getSelectionContext(view);
                if (!definitionId && !onDiagnosticWidget && !selection) return false;

                event.preventDefault();
                event.stopPropagation();
                void (async () => {
                    await showContextMenuAt(view, event);
                })();
                return true;
            },
        }),
    ];
}
